import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useAuth } from '../../context/AuthContext';
import NameModal from '../../components/modals/NameModal';

// Import react-icons
import { FaInstagram, FaYoutube } from "react-icons/fa";
import { FaTiktok } from "react-icons/fa6"; 
import perfilImg from '../../assets/images/perfil.png';
import etImage from '../../assets/bashar/et.png';
import meditationImage from '../../assets/meditation/meditation.png';
import gammaImage from '../../assets/gamma/gamma.png';
import merkabaImg from '../../assets/images/merkaba/Merkaba.png';
import { sendCodedLog } from '../../services/logService';
import Common from '../../common/Common';
import LinktreeButton from '../../components/oracle/LinktreeButton';

// Helper para imagen random
const getRandomCardImage = () => `https://gusaguiar.com/assets/oracle/deck-en/${Math.floor(Math.random() * 89) + 1}.png`;

// --- ITEMS DESTACADOS (ORACLE) ---
const memenuItems = [
  {
    title: "Metaphysical Oracle",
    subtitle: "Options to get guidance through the oracle",
    cardCount: 3, // 3 Cartas
    path: "/oracle/menu",
    featured: true,
  },
  {
    title: "7 Phase Meditation",
    subtitle: "Enter elevated state of beings with this powerful meditaion",
    image: meditationImage,
    path: "/sietefases?lg=en",
    featured: true,
  },
  {
    title: "Wim Hof Breathing",
    subtitle: "Activate your nervous system and elevate your energy",
    image: gammaImage,
    path: "/wimhof?lg=en",
    featured: true,
  },
  {
    title: "Bashar & Metaphysical Teachings",
    subtitle: "Channeled messages and teachings from Bashar",
    image: etImage,
    path: "/bashar/menu?lg=en",
    featured: true,
  },
];

const baseMenuItems = [
];

// --- COMPONENTE DE CARTAS APILADAS (CardStack) ---
const CardStack = ({ count }) => {
  // useMemo asegura que las imágenes sean estáticas y no cambien con cada render
  const cards = useMemo(() => {
    return [...Array(count)].map(() => getRandomCardImage());
  }, [count]);

  return (
    <div className="relative h-full flex items-center justify-center w-full">
      {cards.map((imgSrc, i) => {
        // Lógica de abanico
        const offset = i - Math.floor(count / 2);
        
        const rotation = offset * 8; 
        const translateX = offset * 14; 
        const translateY = Math.abs(offset) * 2; 
        const scale = 1 - (Math.abs(offset) * 0.02); 

        return (
          <img
            key={i}
            src={imgSrc}
            alt={`Card ${i}`}
            // h-full w-auto mantiene el aspect ratio correcto
            className="absolute h-full w-auto object-contain rounded-md shadow-xl border border-gray-600/60 bg-gray-900 transition-transform duration-300 group-hover:scale-110"
            style={{
              zIndex: i, 
              transformOrigin: '50% 90%', // Pivote abajo
              transform: `translateX(${translateX}px) translateY(${translateY}px) rotate(${rotation}deg) scale(${scale})`,
              left: 0,
              right: 0,
              margin: '0 auto'
            }}
          />
        );
      })}
    </div>
  );
};


const HomePageEn = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [showNameModal, setShowNameModal] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [merkabaOpacity, setMerkabaOpacity] = useState(0);
  const [merkabaRotation, setMerkabaRotation] = useState(0);
  const [titleGlowIntensity, setTitleGlowIntensity] = useState(0.2);

  const displayName = user?.name || guestName;

  useEffect(() => {
    const savedGuestName = localStorage.getItem('track_user_name');
    if (savedGuestName) {
      setGuestName(savedGuestName);
    }
    const nameModalDismissed = sessionStorage.getItem('nameModalDismissed');
    if (!user && !nameModalDismissed && !savedGuestName) {
      const timer = setTimeout(() => setShowNameModal(false), 7000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    let currentRotation = 0;
    const animateMerkaba = () => {
      currentRotation = (currentRotation + 30) % 360;
      setMerkabaRotation(currentRotation); setMerkabaOpacity(0.5);
      const fadeOutTimer = setTimeout(() => setMerkabaOpacity(0), 2000);
      const loopTimer = setTimeout(animateMerkaba, 3500);
      return () => { clearTimeout(fadeOutTimer); clearTimeout(loopTimer); };
    };
    const initialDelay = setTimeout(animateMerkaba, 500);
    return () => clearTimeout(initialDelay);
  }, []);

  useEffect(() => {
    const animateGlow = () => setTitleGlowIntensity(prev => (prev === 0.2 ? 0.4 : 0.2));
    const glowInterval = setInterval(animateGlow, 3000);
    return () => clearInterval(glowInterval);
  }, []);

  const handleNameSubmit = (name) => {
    localStorage.setItem('track_user_name', name);
    setGuestName(name);
    setShowNameModal(false);
    sendCodedLog({ message: 'Guest added their name', event: 'guest_name_set', ...Common.getCommonLogData(location) });
  };

  const handleModalClose = () => {
    sessionStorage.setItem('nameModalDismissed', 'true');
    setShowNameModal(false);
    sendCodedLog({ message: 'Guest dismissed the name modal', event: 'guest_modal_name_dismiss', ...Common.getCommonLogData(location) });
  };

  const handleLinktreeButtonClick = (title, path) => {
    const eventMap = { 
        "Portal Oracle": { event: 'home_portal_oracle_button_click_en', msg: 'Click on "Portal Oracle" button' },
        "Going Gamma": { event: 'home_going_gamma_button_click_en', msg: 'Click on "Going Gamma" button' }, 
        "Bashar Transmissions": { event: 'home_bashar_button_click_en', msg: 'Click on "Bashar Transmissions" button' }, 
        "Manifestation Technics Book": { event: 'home_manifestation_book_button_click_en', msg: 'Click on "Manifestation Technics Book" button' }, 
        "Continue My Course": { event: 'home_continue_course_button_click_en', msg: 'Click on "Continue My Course" button' } 
    };
    const logData = eventMap[title] || { event: 'home_generic_button_click_en', msg: `Click on "${title}" button` };
    sendCodedLog({ message: `${logData.msg} (${path})`, event: logData.event, ...Common.getCommonLogData(location) });
  };
  
  const handleProfileImageClick = () => sendCodedLog({ message: 'Click on profile image', event: 'home_profile_image_click_en', ...Common.getCommonLogData(location) });
  
  const handleSocialMediaClick = (platform, url) => {
    sendCodedLog({ message: `Social media link click (Home): ${platform}`, event: `home_social_media_click_${platform.toLowerCase()}_en`, ...Common.getCommonLogData(location) });
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  const handleInstagramClick = (e) => { e.preventDefault(); handleSocialMediaClick('Instagram', 'https://instagram.com/quantum.gus'); };
  const handleTiktokClick = (e) => { e.preventDefault(); handleSocialMediaClick('TikTok', 'https://www.tiktok.com/@quantum.gus'); };
  const handleYoutubeClick = (e) => { e.preventDefault(); handleSocialMediaClick('YouTube', 'https://www.youtube.com/@imanifesti'); };

  // Unimos los menús (nuevos oráculos + base)
  const menuItems = [...memenuItems, ...baseMenuItems];

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-neutral-900 to-black"></div>

      <main className="relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center">

        <div className="relative flex items-center justify-between mb-8 w-full max-w-2xl mx-auto">
          <h1 className="relative z-10 text-3xl sm:text-6xl font-bold leading-none text-left mt-7 mb-7" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #FFD700, #DAA520, #B8860B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: `0 0 8px rgba(255, 215, 0, ${titleGlowIntensity}), 0 0 12px rgba(255, 215, 0, ${titleGlowIntensity / 2})`, transition: 'text-shadow 1.5s ease-in-out' }}>
            Portal
          </h1>
          <img src={merkabaImg} alt="Golden Merkaba" className="absolute z-0 w-36 h-36 sm:w-44 sm:h-44 object-contain rounded-full transition-all duration-1000 ease-in-out" style={{ opacity: merkabaOpacity, top: '50%', left: '50%', transform: `translate(-50%, -50%) rotate(${merkabaRotation}deg)` }} />
          <h1 className="relative z-10 text-3xl sm:text-6xl font-bold leading-none text-right mt-7 mb-7" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #FFD700, #DAA520, #B8860B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: `0 0 8px rgba(255, 215, 0, ${titleGlowIntensity}), 0 0 12px rgba(255, 215, 0, ${titleGlowIntensity / 2})`, transition: 'text-shadow 1.5s ease-in-out' }}>
            Oracle
          </h1>
        </div>

        <div className="text-center mb-10">
            <div className="relative flex items-baseline justify-center gap-2">
                <h2 className="relative z-10 text-2xl sm:text-2xl font-bold leading-none" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #c4c1b3ff, #e4d4adff, #e7d9b6ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Hi,
                </h2>
                <h1 className="relative z-10 text-3xl sm:text-4xl font-bold leading-none" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #c4c1b3ff, #e4d4adff, #e7d9b6ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: `0 0 8px rgba(255, 215, 0, ${titleGlowIntensity}), 0 0 12px rgba(255, 215, 0, ${titleGlowIntensity / 2})`, transition: 'text-shadow 1.5s ease-in-out' }}>
                    {displayName || 'Traveller'}
                </h1>
            </div>
            <p className="text-sm sm:text-base text-gray-400 mt-3 max-w-sm mx-auto">
                {user
                    ? "Resume your transformation journey."
                    : guestName
                        ? `Welcome, ${guestName}! Get guidance through the Portal Oracle.`
                        : "Get guidance through the Portal Oracle."
                }
            </p>
        </div>
        
        <div className="w-full flex flex-col items-center">
          {menuItems.map((item, index) => (
            <LinktreeButton key={item.path + index} item={item} index={index} onButtonClick={handleLinktreeButtonClick} />
          ))}
        </div>

        {!displayName && (
            <div className="flex flex-col items-center mt-12 mb-8">
                <img
                    src={perfilImg}
                    alt="Profile" 
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-full mb-2 object-cover border-4 border-gray-700 shadow-lg cursor-pointer"
                    onClick={handleProfileImageClick}
                />
                <h2 className="text-xl sm:text-2xl font-bold mb-0.5 text-white">
                    Gustavo • Aguiar
                </h2>
                <p className="text-sm sm:text-base text-gray-400 text-center max-w-sm mb-8">
                    Writer & Coach <br/> I explain the 5 Universal Laws.
                </p>
            </div>
        )}

        <div className="flex justify-center gap-6 text-2xl text-gray-300 mt-8">
          <a onClick={handleInstagramClick} aria-label="Instagram" className="hover:text-white cursor-pointer"><FaInstagram /></a>
          <a onClick={handleTiktokClick} aria-label="TikTok" className="hover:text-white cursor-pointer"><FaTiktok /></a>
          <a onClick={handleYoutubeClick} aria-label="YouTube" className="hover:text-white cursor-pointer"><FaYoutube /></a>
        </div>
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
        <p>© {new Date().getFullYear()} Gus • Aguiar | On a mission to elevate and transform.</p>
      </footer>

      {showNameModal && <NameModal onClose={handleModalClose} onSubmitName={handleNameSubmit} />}
    </div>
  );
};

export default HomePageEn;